/**
 * TradeHistoryTable
 * 历史成交记录表格组件
 * 展示用户的模拟交易成交记录
 *
 * @author page
 * @date 2026-04-22
 */
import React, {useEffect, useState} from 'react';
import {App, Spin, Table, Tabs, Tag} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {simulateTradingService} from '../../services/simulateService';
import type {SimulateTrade} from '../../types/simulate';


interface TradeHistoryTableProps {
    autoRefreshInterval?: number;
    /**
     * 作为 Tabs 的一部分，接收 PositionTable 组件，
     * 以便在一个卡片中同时展示当前持仓和交易历史。
     */
    positionTable?: React.ReactNode;
}

const TradeHistoryTable: React.FC<TradeHistoryTableProps> = ({
                                                                 autoRefreshInterval = 0,
                                                                 positionTable,
                                                             }) => {
    const {message: messageApi} = App.useApp();
    const [trades, setTrades] = useState<SimulateTrade[]>([]);
    const [loading, setLoading] = useState(false);

    const loadTrades = async () => {
        try {
            setLoading(true);
            const res = await simulateTradingService.getTrades();
            const data = res.data?.data || [];
            setTrades(Array.isArray(data) ? data : []);
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '获取交易记录失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTrades();
    }, []);

    useEffect(() => {
        if (autoRefreshInterval > 0) {
            const timer = setInterval(loadTrades, autoRefreshInterval);
            return () => clearInterval(timer);
        }
    }, [autoRefreshInterval]);

    const columns: ColumnsType<SimulateTrade> = [
        {
            title: '交易对',
            dataIndex: 'symbol',
            key: 'symbol',
            width: 120,
            fixed: 'left' as const,
            render: (text) => <span style={{color: '#fff', fontWeight: 500}}>{text}</span>,
        },
        {
            title: '方向',
            dataIndex: 'side',
            key: 'side',
            width: 80,
            align: 'center' as const,
            render: (val: number) => (
                <Tag color={val === 0 ? 'success' : 'error'} style={{
                    border: 'none',
                    background: val === 0 ? 'rgba(82, 196, 26, 0.15)' : 'rgba(255, 77, 79, 0.15)'
                }}>
                    {val === 0 ? '买入' : '卖出'}
                </Tag>
            ),
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            width: 80,
            align: 'center' as const,
            render: (val: number) => <span
                style={{color: 'rgba(255,255,255,0.85)'}}>{val === 0 ? '市价' : '限价'}</span>,
        },
        {
            title: '成交价格',
            dataIndex: 'price',
            key: 'price',
            width: 100,
            align: 'right' as const,
            render: (val: string) => <span
                style={{color: '#fff', fontFamily: 'Monaco'}}>${Number(val).toFixed(2)}</span>,
        },
        {
            title: '数量',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            align: 'right' as const,
            render: (val: string) => <span
                style={{color: '#fff', fontFamily: 'Monaco'}}>{Number(val).toFixed(4)}</span>,
        },
        {
            title: '成交金额',
            dataIndex: 'quoteAmount',
            key: 'quoteAmount',
            width: 100,
            align: 'right' as const,
            render: (val: string) => <span
                style={{color: '#fff', fontFamily: 'Monaco'}}>${Number(val).toFixed(2)}</span>,
        },
        {
            title: '手续费',
            dataIndex: 'fee',
            key: 'fee',
            width: 80,
            align: 'right' as const,
            render: (val: string) => <span
                style={{color: 'rgba(255,255,255,0.65)', fontFamily: 'Monaco'}}>${Number(val).toFixed(2)}</span>,
        },
        {
            title: '成交时间',
            dataIndex: 'tradeTime',
            key: 'tradeTime',
            width: 180,
            render: (text) => <span style={{color: 'rgba(255,255,255,0.65)'}}>{text}</span>,
        },
    ];

    // 自定义表格样式覆盖，适配暗黑主题
    const darkTableStyle = {
        style: {
            backgroundColor: 'var(--pt-bg-panel)',
            color: '#fff'
        }
    };

    // 如果传入了 positionTable，就使用 Tabs 包裹两个表格
    // 否则仅展示交易历史（向下兼容）
    if (positionTable) {
        const items = [
            {
                key: 'positions',
                label: <span style={{color: '#fff'}}>当前持仓</span>,
                children: positionTable,
            },
            {
                key: 'history',
                label: <span style={{color: '#fff'}}>历史成交</span>,
                children: (
                    <Spin spinning={loading}>
                        <Table
                            className="dark-theme-table"
                            columns={columns}
                            dataSource={trades}
                            rowKey="id"
                            pagination={false}
                            scroll={{x: 800, y: 400}}
                            locale={{emptyText: '暂无交易记录'}}
                            size="small"
                            onRow={() => darkTableStyle}
                        />
                    </Spin>
                ),
            }
        ];

        return (
            <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <Tabs
                    items={items}
                    className="dark-theme-tabs"
                    tabBarStyle={{padding: '0 16px', margin: 0, borderBottom: '1px solid var(--pt-border)'}}
                />
            </div>
        );
    }

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Spin spinning={loading}>
                <Table
                    className="dark-theme-table"
                    columns={columns}
                    dataSource={trades}
                    rowKey="id"
                    pagination={false}
                    scroll={{x: 800}}
                    locale={{emptyText: '暂无交易记录'}}
                    size="small"
                    onRow={() => darkTableStyle}
                />
            </Spin>
        </div>
    );
};

export default TradeHistoryTable;